import { Box, Button, Card, CircularProgress, Container, Typography, Alert, MenuItem } from "@mui/material";
import { useState, useEffect, useMemo } from "react";
import { enqueueSnackbar } from "notistack";
import axios, { endpoints, formatEndpoint } from 'src/utils/axios';
import { useSettingsContext } from "src/components/settings";
import Iconify from "src/components/iconify/iconify";
import { addDaysToDate, formatDateForPreview, today } from "src/utils/date-utils";
import TruxSimpleDateField from "src/components/inputs/trux-simple-date-field";
import { ColoredIcon } from "src/components/colored-icon";
import TruxSimpleTextField from "src/components/inputs/trux-simple-text-field";
import { useRouter, useSearchParams } from "src/routes/hooks";
import TruxSimpleSelectField from "src/components/inputs/trux-simple-select-field";
import { useAuthContext } from "src/auth/hooks";

export default function Page() {
  const { session } = useAuthContext();
  const settings = useSettingsContext();
  const router = useRouter();

  const searchParams = useSearchParams();
  const barcode = searchParams.get("barcode");

  const [deviceCode, setDeviceCode] = useState("");
  const [deviceName, setDeviceName] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<number | undefined>(undefined);

  useEffect(() => {
    if ((window as any).flutter_inappwebview?.postMessage && !barcode) {
      (window as any).flutter_inappwebview.postMessage(JSON.stringify({
        type: 'activate_device',
      }));
      router.back();
    }
  }, [router, barcode]);

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <Alert icon={<Box sx={{display:"flex", alignItems: "center", justifyContent: "center"}}><Iconify icon="line-md:phone" /></Box>} severity="success" sx={{mb: 2}}>
        To get your asset tracking devices today reach out to (650) 549-9010 or email sales@countingtrucks.com.
      </Alert>

      <Card sx={{p: 4, display: "flex", flexDirection:"column", alignItems: "center", gap: 4}}>
        <Typography variant="h4">
          Activate a Counting Trucks Device
        </Typography>

        <Box maxWidth="600px" width="100%" display="flex" flexDirection="column" alignItems="center" gap={2}>
          {!barcode && <>

            <Box sx={{display: "flex", rowGap: "12px", columnGap: "12px", alignItems: "center", flexWrap: "wrap", justifyContent: "center"}}>
              <a href="https://apps.apple.com/sg/app/counting-trucks-manager/id6740706940?platform=iphone">
                <img src="/assets/map/app_store.png" alt="" style={{maxHeight: "60px"}} />
              </a>

              <a href="https://play.google.com/store/apps/details?id=com.countingtrucksmanager.ctmanager&hl=en_US">
                <img src="/assets/map/play_store.png" alt="" style={{maxHeight: "60px"}}  />
              </a>
            </Box>

            <Box textAlign="center">
              To add devices using the barcode scanner please open the Counting Trucks Manager app.
            </Box>

            <Box sx={{display:"flex", pt: 4, justifyContent:"center", alignItems: "center", width: "100%", rowGap: "15px", columnGap: "15px"}}>
              <Box sx={{flexGrow: "1", borderTop: "1px solid #ddd"}} />
              <Box>or</Box>
              <Box sx={{flexGrow: "1", borderTop: "1px solid #ddd"}} />
            </Box>
          </>}

          <Box width="100%">
            <Typography component="div" fontWeight={700}>Barcode</Typography>
            {barcode ?
              <TruxSimpleTextField value={barcode} disabled onChange={() => {}} />
              : <TruxSimpleTextField value={deviceCode} onChange={d => setDeviceCode(d!)} />}
          </Box>

          {session?.organization?.isParent && <Box width="100%">
            <Typography component="div" fontWeight={700}>Organization</Typography>
            <TruxSimpleSelectField value={selectedOrganizationId} onChange={d => setSelectedOrganizationId(d!)}>
              {session.user.organizationNames.map((o, index) => (
                <MenuItem key={o} value={session.user.organizationIds[index]}>{o}</MenuItem>
              ))}
            </TruxSimpleSelectField>
          </Box>}

          <Box width="100%">
            <Typography component="div" fontWeight={700}>Custom Asset Name/Number</Typography>
            <TruxSimpleTextField value={deviceName} onChange={d => setDeviceName(d!)} />
          </Box>

          <Box width="100%">
            {loading ? <Box display="flex" justifyContent="center"><CircularProgress size={30} /></Box> :
              <Box sx={{display: "flex", justifyContent: "end"}}><Button variant="contained" color="success" disabled={loading} sx={{mt:2}} startIcon={<Iconify icon="lucide:check" />} onClick={() => {
                if (session?.organization?.isParent) {
                  if (!selectedOrganizationId) {
                    enqueueSnackbar("Please select an organization", {variant: "error"});
                    return;
                  }
                }

                setLoading(true);
                axios.post(endpoints.devices.activate, {
                  deviceCode: barcode || deviceCode,
                  deviceType: deviceName,
                  organizationId: selectedOrganizationId,
                }).then((response) => {
                  if (response.data.errors) {
                    response.data.errors.forEach((e: string) => enqueueSnackbar(e, {variant: "error"}));
                  } else {
                    enqueueSnackbar("Successfully Activated the device!");
                    setDeviceCode("");
                    setDeviceName("");
                  }
                }).catch((e) => {
                  if (e.response?.data?.errors?.length) {
                    e.response.data.errors.forEach((m: string) => enqueueSnackbar(m, {variant: "error"}));
                  } else {
                    enqueueSnackbar('An unexpected error has occured', { variant: 'error' });
                  }
                }).finally(() => {
                  setLoading(false);
                })
              }}>
                Activate
              </Button></Box>}
          </Box>
        </Box>
      </Card>
    </Container >
  );
}
